import {
  FC,
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react'
import { Person, StringKeys } from 'models'
import { Loader } from 'components/loader'
import { AnimatePresence } from 'framer-motion'
import { localizeColumns, processStrings } from 'lang/helpers'

export type AppContextType = {
  data: Person[]
  strings: { [k in StringKeys]: string }
}

const AppContext = createContext<AppContextType | null>(null)

export const AppContextProvider: FC<{
  children?: ReactNode | ReactNode[]
}> = ({ children }) => {
  const [isLoading, setLoading] = useState(true)
  const [data, setData] = useState<AppContextType>({
    data: [],
    strings: {} as AppContextType['strings'],
  })

  const fetchData = async () => {
    const d = await fetch(`${process.env.PUBLIC_URL}/data.json`, {
      cache: 'no-cache',
    })
      .then(res => res.text())
      .then(res => {
        return JSON.parse(res.replaceAll(/\n/g, ''))
      })

    setData({
      data: localizeColumns(d.data, d.enums),
      strings: processStrings(d.strings),
    })
  }

  const handleLoad = async () => {
    await Promise.all([
      fetchData(),
      new Promise(resolve => {
        setTimeout(resolve, 500)
      }),
    ])

    setLoading(false)
  }

  useEffect(() => {
    void handleLoad()
  }, [])

  return (
    <AppContext.Provider value={data}>
      <AnimatePresence mode="wait">{isLoading && <Loader />}</AnimatePresence>
      {!isLoading && children}
    </AppContext.Provider>
  )
}

export const useAppContext = () => {
  const context = useContext(AppContext)
  return context as AppContextType
}
